import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Card, Group, Stack, Switch, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPuzzle } from '@tabler/icons-react'
import {
  CORE_MODULE_NAME,
  ensureCoreInList,
  licenseFeatureListIncludes,
  listHostedWebModuleSettingsRows,
  normalizeOwlbertModuleKey,
} from '@owlbert/core'
import { useOwlbertWebAuth } from '../../auth/OwlbertWebAuthContext'
import { HOSTED_WORKSPACE_MODULES_CHANGED } from '../../hooks/useHostedWorkspaceNavModules'

export const WebModulesSettingsPanel: React.FC = () => {
  const auth = useOwlbertWebAuth()
  const workspaceId = auth.workspaceId
  const client = auth.bundle?.client
  const features = auth.entitlement?.features ?? []

  const rows = useMemo(() => listHostedWebModuleSettingsRows(), [])
  const [enabled, setEnabled] = useState<string[]>([CORE_MODULE_NAME])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const reload = useCallback(async () => {
    if (!client || !workspaceId) {
      setEnabled([CORE_MODULE_NAME])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const res = await client.owlbertHosted.workspaceModules.get.query({ workspaceId })
      setEnabled(res.enabled_modules)
    } catch (e: unknown) {
      notifications.show({
        title: 'Failed to load modules',
        message: e instanceof Error ? e.message : 'Unknown error',
        color: 'red',
      })
    } finally {
      setLoading(false)
    }
  }, [client, workspaceId])

  useEffect(() => {
    void reload()
  }, [reload])

  const persist = async (next: string[]) => {
    if (!client || !workspaceId) return
    setSaving(true)
    try {
      const res = await client.owlbertHosted.workspaceModules.set.mutate({
        workspaceId,
        enabled_modules: next,
      })
      setEnabled(res.enabled_modules)
      window.dispatchEvent(new Event(HOSTED_WORKSPACE_MODULES_CHANGED))
    } catch (e: unknown) {
      notifications.show({
        title: 'Could not save modules',
        message: e instanceof Error ? e.message : 'Unknown error',
        color: 'red',
      })
      await reload()
    } finally {
      setSaving(false)
    }
  }

  const onToggle = async (
    canonicalName: string,
    isCore: boolean,
    licenseFeatureId: string,
    checked: boolean,
  ) => {
    if (isCore || canonicalName === CORE_MODULE_NAME) return
    const licensed = !licenseFeatureId || licenseFeatureListIncludes(features, licenseFeatureId)
    if (!licensed) {
      notifications.show({
        title: 'Not available',
        message: 'Your account does not include a license for this module.',
        color: 'yellow',
      })
      return
    }
    const key = (name: string) => normalizeOwlbertModuleKey(name)
    let base = [...enabled]
    if (checked) {
      if (!base.some((m) => key(m) === key(canonicalName))) base.push(canonicalName)
    } else {
      base = base.filter((m) => key(m) !== key(canonicalName))
    }
    const withCore = ensureCoreInList(base)
    const ordered = rows
      .filter((r) => withCore.some((m) => key(m) === key(r.canonicalName)))
      .map((r) => r.canonicalName)
    setEnabled(ordered)
    await persist(ordered)
  }

  if (!auth.sessionReady) {
    return (
      <Text size="sm" c="dimmed" maw={720}>
        Sign in to choose which modules are enabled for your hosted workspace.
      </Text>
    )
  }

  if (!workspaceId) {
    return (
      <Alert color="yellow" title="Pick a workspace">
        Choose a workspace in General before changing modules.
      </Alert>
    )
  }

  return (
    <Stack gap="lg" maw={960}>
      <Card withBorder p="lg" radius="md">
        <Group gap="sm" mb="md">
          <IconPuzzle size={22} />
          <Text fw={700} size="lg">
            Modules
          </Text>
        </Group>
        <Text size="sm" c="dimmed" mb="md">
          Turn optional modules on or off for this workspace. Settings sync with Owlbert cloud; the web app shows a
          static catalog (plugins are not loaded in the browser). Core stays on. Changes are saved when you toggle a
          switch.
        </Text>
        <Stack gap="md">
          {rows.map((row) => {
            const licensed =
              row.isCore || !row.licenseFeatureId || licenseFeatureListIncludes(features, row.licenseFeatureId)
            const checked =
              row.isCore ||
              enabled.some((m) => normalizeOwlbertModuleKey(m) === normalizeOwlbertModuleKey(row.canonicalName))
            return (
              <Group key={row.canonicalName} justify="space-between" align="flex-start" wrap="nowrap" gap="md">
                <div style={{ minWidth: 0 }}>
                  <Text fw={600} size="sm">
                    {row.displayName}
                  </Text>
                  <Text size="xs" c="dimmed" lineClamp={4}>
                    {row.description}
                  </Text>
                  {!row.isCore && !licensed ? (
                    <Text size="xs" c="orange" mt={4}>
                      Not licensed on this account.
                    </Text>
                  ) : null}
                </div>
                <Switch
                  checked={checked}
                  disabled={loading || saving || row.isCore || !licensed}
                  onChange={(e) =>
                    void onToggle(row.canonicalName, row.isCore, row.licenseFeatureId, e.currentTarget.checked)
                  }
                  aria-label={`${row.displayName} module`}
                />
              </Group>
            )
          })}
        </Stack>
      </Card>
    </Stack>
  )
}
